import { FILE_PREFIX, postRequest, TRIP_PREFIX } from 'global/helpers/requests'
import type { AttachmentData, StopLegPair } from 'screens/TripDetails/TripDetails.types'
import type {
  BulkDocumentsUploadFormValues,
  BulkDocumentsUploadResponse,
  BulkStatusUpdateFormValues,
  DocumentsUploadFormValues,
  StatusUpdateFormValues,
  StatusUpdateWithAttachmentsFormValues,
} from './StatusManager.types'

export interface UploadFileResponse {
  id: string
  fileName: string
  checksum: string
  size: number
  timestamp: number
  isDocument: boolean
}

export const updateStopStatus = async (
  tripCid: string,
  wayPointCid: string,
  data: StatusUpdateFormValues,
  weblinkToken?: string,
): Promise<void> => {
  // NOTE: data without intermediate values
  const processedData = {
    wayPointType: data.wayPointType,
    statusCode: data.statusCode,
    reasonCode: data.reasonCode,
    createdAt: data.createdAt,
    shipmentNumber: data.shipmentNumber,
    comment: data.comment,
    signee: data.signee,
    signature: data.signature,
  }
  return await postRequest<void>(
    `trips/${tripCid}/waypoints/${wayPointCid}/status`,
    { body: processedData, token: weblinkToken },
    TRIP_PREFIX,
  )
}

export const updateStopStatusWithAttachments = async (
  tripCid: string,
  wayPointCid: string,
  data: StatusUpdateWithAttachmentsFormValues,
  weblinkToken?: string,
): Promise<void> => {
  // NOTE: data without intermediate values
  const processedData = {
    wayPointType: data.wayPointType,
    statusCode: data.statusCode,
    reasonCode: data.reasonCode,
    createdAt: data.createdAt,
    shipmentNumber: data.shipmentNumber,
    comment: data.comment,
    signee: data.signee,
    signature: data.signature,
    attachments: processAttachments(data),
  }
  return await postRequest<void>(
    `trips/${tripCid}/waypoints/${wayPointCid}/status-with-attachments`,
    { body: processedData, token: weblinkToken },
    TRIP_PREFIX,
  )
}

export const bulkUpdateStopStatus = async (
  tripCid: string,
  stopLegPairs: StopLegPair[],
  data: BulkStatusUpdateFormValues,
  weblinkToken?: string,
): Promise<void> => {
  // NOTE: data without intermediate values
  const processedData = {
    updates: stopLegPairs
      .filter((stopLegPair) => data.shipmentNumbers.includes(stopLegPair.leg.shipmentNumber))
      .map((stopLegPair) => ({
        wayPointCid: stopLegPair.stop.wayPointCid,
        wayPointType: data.wayPointType,
        statusCode: data.statusCode,
        reasonCode: data.reasonCode,
        createdAt: data.createdAt,
        shipmentNumber: stopLegPair.leg.shipmentNumber,
        comment: data.comment,
        signee: data.signee,
        signature: data.signature,
        attachments: [], // see note below
      })),
  }
  // NOTE: it uses status-with-attachments endpoint to properly process paperless,
  // but so far actual document upload isn't supported yet and isn't visible in the form
  return await postRequest<void>(
    `trips/${tripCid}/waypoints/status-with-attachments`,
    { body: processedData, token: weblinkToken },
    TRIP_PREFIX,
  )
}

export const minFileSize = 1024
export const maxFileSize = 1024 * 1024 * 10

export const uploadFile = async (file: File, weblinkToken?: string): Promise<UploadFileResponse> => {
  const formData = new FormData()
  formData.append('form', file)
  return await postRequest<UploadFileResponse>(
    'files',
    { body: formData, token: weblinkToken },
    FILE_PREFIX,
  )
}

export const attachFiles = async (
  tripCid: string,
  wayPointCid: string,
  data: DocumentsUploadFormValues,
  weblinkToken?: string,
): Promise<void> => {
  const processedData = {
    attachments: processAttachments(data),
  }
  await postRequest<void>(
    `trips/${tripCid}/waypoints/${wayPointCid}/attachments`,
    { body: processedData, token: weblinkToken },
    TRIP_PREFIX,
  )
}

export const bulkAttachFiles = async (
  tripCid: string,
  stopLegPairs: StopLegPair[],
  data: BulkDocumentsUploadFormValues,
  weblinkToken?: string,
): Promise<BulkDocumentsUploadResponse> => {
  const wayPointCids = stopLegPairs
    .filter((stopLegPair) => data.shipmentNumbers.includes(stopLegPair.leg.shipmentNumber))
    .map((stopLegPair) => stopLegPair.stop.wayPointCid)
  const processedData = {
    attachments: processAttachments(data),
    waypointCids: wayPointCids,
  }
  return await postRequest<BulkDocumentsUploadResponse>(
    `trips/${tripCid}/waypoints/attachments`,
    { body: processedData, token: weblinkToken },
    TRIP_PREFIX,
  )
}

export const processAttachments = (data: DocumentsUploadFormValues): AttachmentData[] => {
  const attachments: AttachmentData[] = []
  if (data.popFiles) {
    for (const popId of data.popFiles) {
      attachments.push({ fileId: popId, fileType: '507' }) // 507=POP
    }
  }
  if (data.podFiles) {
    for (const podId of data.podFiles) {
      attachments.push({ fileId: podId, fileType: '545' }) // 545=POD
    }
  }
  if (data.photoFiles) {
    for (const photoId of data.photoFiles) {
      attachments.push({ fileId: photoId, fileType: '991' }) // 991=IMG
    }
  }
  if (data.palletFile) {
    attachments.push({ fileId: data.palletFile, fileType: '535' }) // 535=pallet slip
  }
  if (data.troFile) {
    attachments.push({ fileId: data.troFile, fileType: '387' }) // 387=TRO
  }
  return attachments
}
